import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Divider, Drawer, notification, Result, Row } from 'antd';
import { Route, Switch, useHistory } from 'react-router';
import { Link, matchPath, useLocation, useParams } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { useGlobalState } from './components/GlobalState';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import rightArrow from '../home/assets/images/right-arrow 10.svg';
import ship from '../home/assets/images/AP-ship.png';
import shop from '../home/assets/images/shop-image.png';

import {
  HeartFilled,
  HeartOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';

import LoadingData from '../common/LoadingData';
import DataError from '../common/DataError';
import FeatureSummary from './components/FeatureSummary';
import MarkdownContent from '../common/MarkdownContent';
import ContactInfo from './components/ContactInfo';
import {
  ImageSlider,
  LakesSlider,
  ReviewsSlider,
  CatchReportSlider,
} from '../common/components/sliders';
import { MapWithAddressPin } from './components/MapWithAddressPin';
import ReviewsHeader from '../common/components/reviews_visualisation/ReviewsHeader';
import CountedReviewsScore from '../common/components/reviews_visualisation/CountedReviewsScore';
import {
  FisheryProfileSection as Section,
  FisheryProfileSubSection as SubSection,
} from './components/FisheryProfileSection';
import {
  BackButtonLayout,
  MainPageBackButtonLayout,
} from '../../containers/BackButtonLayout';
import HowToBookGuide from './components/HowToBookGuide';
import BookableTag from './components/BookableTag';
import FisheryProfileQuickLinks, {
  LabelledRoundButton,
} from './components/FisheryProfileQuickLinks';
import ReviewsList from './components/ReviewsList';
import NewReviewForm from './components/NewReviewForm';
import LakeDetails from './components/LakeDetails';
import FisheryProfileFooterBar, {
  FooterContentsOption,
} from './components/FisheryProfileFooterBar';
import SwimViewer from './components/SwimViewer';
import PrimarySecondaryImageGallery from '../common/PrimarySecondaryImageGallery';
import { exotic_fishery } from '../common/Utils';

import {
  useGetFisheryQuery,
  useGetMembershipPlansQuery,
  useGetUserMembershipsQuery,
  useMarkFisheryAsFavouriteMutation,
} from './fisherySlice';
import { PlaceholderImage } from '../common/components/sliders/components/SliderImages';
import SBOpenWeather from './components/SBOpenWeather';
import InlineChevronIcon from '../../assets/images/InlineChevronIcon';
import SocialMediaShareModal from '../common/components/social_media_modal/SocialMediaShareModal';

import youtubeIcon from '../common/assets/images/yt_logo_rgb_full.png';
import memberIcon from './assets/images/member-icon.svg';
import ticketIcon from './assets/images/tickets-icon.svg';
import './assets/css/fishery_profile.css';
import { usePreventScroll, useQuery } from '../../utils/hooks';
import LiveAvailability from './components/LiveAvailability';
import { useDispatch, useSelector } from 'react-redux';

import brevoChatIcon from '../../assets/images/brevo-chat-icon.svg';
import ArrowRight from '../../assets/images/fishery_profile/icons/ArrowRight.png';
import { MENU } from '../../AppConstants';
import MembershipDrawer from './components/MembershipDrawer';
const { TOGGLE_CATCH_REPORT, TOGGLE_AP_CROSSING } = MENU;

function checkIfUserLoggedIn(setIsLoggedIn) {
  const axios_config = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  };

  axios
    .get('auth', axios_config)
    .then((res) => {
      if (res.status === 200) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
    })
    .catch(() => {
      setIsLoggedIn(false);
    });
}

export default function FisheryProfile() {
  const dispatch = useDispatch();
  const fisheryId = useParams()['fishery_id'];
  const drawerContentsKey = useParams()['drawer_key'];

  // Empty dependency array means this runs once on mount

  const { pathname } = useLocation();
  const isWebview = useQuery().get('isWebview');
  const isMarketing = useQuery().get('marketing');
  const purchaseDrawerType = useQuery().get('type');
  const history = useHistory();

  const [isLoggedIn, setIsLoggedIn] = useGlobalState('isLoggedIn');
  const [catchReport, setCatchReport] = useState([]);
  const [membershipForm, setMembershipForm] = useState();
  const [membershipDrawerOpen, setMembershipDrawerOpen] = useState(false);
  const {
    data: fisheryData,
    isLoading: loading,
    isError: errorLoadingFisheryData,
    error: fisheryError,
  } = useGetFisheryQuery(fisheryId);
  const { data: fisheryMembershipData } = useGetMembershipPlansQuery(
    {
      data: fisheryData?.fishery_id,
    },
    { skip: !fisheryData?.fishery_id }
  );

  const { data: userMembershipData } = useGetUserMembershipsQuery(
    { data: '' },
    { skip: !isLoggedIn }
  );
  const europeanCountries = [
    'Albania',
    'Andorra',
    'Armenia',
    'Austria',
    'Azerbaijan',
    'Belarus',
    'Belgium',
    'Bosnia and Herzegovina',
    'Bulgaria',
    'Croatia',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Estonia',
    'Finland',
    'France',
    'Georgia',
    'Germany',
    'Greece',
    'Hungary',
    'Iceland',
    'Ireland',
    'Italy',
    'Kosovo',
    'Latvia',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Malta',
    'Moldova',
    'Monaco',
    'Montenegro',
    'Netherlands',
    'North Macedonia',
    'Norway',
    'Poland',
    'Portugal',
    'Romania',
    'San Marino',
    'Serbia',
    'Slovakia',
    'Slovenia',
    'Spain',
    'Sweden',
    'Switzerland',
    'Ukraine',
    'Vatican City',
  ];

  useEffect(() => {
    if (!fisheryError) {
      return;
    }
    if (fisheryError.status === 404) {
      history.replace('/404');
    }
  }, [history, fisheryError]);

  const fisheryPublicId = fisheryData?.public_id ?? fisheryId;
  const isDirectoryProfile = Boolean(
    fisheryData && !fisheryData?.lakes?.length
  );
  const isPackageBooking = Boolean(
    fisheryData && fisheryData?.supports_package
  );
  const isMemberOnly =
    !isDirectoryProfile &&
    (fisheryData?.lakes ?? [null]).every(
      (l) => l?.access_type === 'Member only'
    );
  const is_sandhurst =
    fisheryPublicId === +process.env.REACT_APP_FISHERY_SANDHURST_ID;
  // Only show components which are present, if no components are available try using address.one_line
  const addressComponents = [
    fisheryData?.address?.city,
    fisheryData?.address?.postcode,
  ].filter((c) => c);
  const address = addressComponents.length
    ? addressComponents.join(', ')
    : fisheryData?.address?.one_line;

  const [markAsFavourite] = useMarkFisheryAsFavouriteMutation();

  const getDefaultFooter = useCallback(
    (isLoggedIn) => {
      if (isDirectoryProfile) {
        return FooterContentsOption.ClaimProfile;
      }
      if (!isLoggedIn) {
        if (isMemberOnly) {
          return FooterContentsOption.MembershipRequiredGuest;
        }
        return FooterContentsOption.BookNowGuest;
      }
      return FooterContentsOption.BookNow;
    },
    [isDirectoryProfile, isMemberOnly]
  );
  const [footerContentsKey, setFooterContentsKey] = useState(
    getDefaultFooter(isLoggedIn)
  );

  const [showShareModal, setShowShareModal] = useState(false);
  const [alreadyMember, setalreadyMember] = useState(false);
  function getCatchReport(fisheryId) {
    const axios_config = {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      params: { id: fisheryId },
    };

    axios
      .get('fishery/catch/report', axios_config)
      .then((res) => {
        if (res.status === 200) {
          const filteredCatchReports = res?.data?.result.filter(
            (catchReport) => catchReport.is_approved && !catchReport.is_deleted
          );
          setCatchReport(filteredCatchReports);
        }
      })
      .catch(() => {});
  }
  useEffect(() => {
    checkIfUserLoggedIn(setIsLoggedIn);
  }, [setIsLoggedIn]);

  useEffect(() => {
    setFooterContentsKey(getDefaultFooter(isLoggedIn));
  }, [getDefaultFooter, isLoggedIn]);

  useEffect(() => {
    if (fisheryData) {
      getCatchReport(fisheryData?.fishery_id);
    }
  }, [fisheryData]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (
      (isWebview === 'yes' || isMarketing === 'yes') &&
      (purchaseDrawerType == 'member' ||
        purchaseDrawerType == 'season_ticket') &&
      fisheryMembershipData
    ) {
      setMembershipDrawerOpen(true);
      let memberData =
        purchaseDrawerType == 'member'
          ? userMembershipData?.memberships
          : userMembershipData?.season_tickets;
      const hasFisheryId = memberData?.filter(
        (data) => data.fishery_id == fisheryId && data?.status !== 'Deleted'
      );
      setalreadyMember(hasFisheryId);
      setMembershipForm(
        fisheryMembershipData?.filter(
          (item) => item.type === purchaseDrawerType
        )
      );
    }
  }, [fisheryMembershipData, userMembershipData]);

  const isDrawerOpen = Boolean(drawerContentsKey);

  // Prevent background app scroll when drawer is open
  usePreventScroll('FisheryProfileDrawer', isDrawerOpen);

  if (loading) {
    return (
      <MainPageBackButtonLayout>
        <LoadingData />
      </MainPageBackButtonLayout>
    );
  }

  if (errorLoadingFisheryData) {
    return (
      <MainPageBackButtonLayout>
        <DataError />
      </MainPageBackButtonLayout>
    );
  }

  const SingleReview = ({ reviewsData }) => {
    const reviewId = useParams()['review_id'];

    const reviewData = reviewsData?.[reviewId - 1];

    if (!reviewId || !reviewData) {
      return <DataError useGenericMessage />;
    }

    return <ReviewsList reviewsData={[reviewData]} />;
  };

  const readMoreDrawer = (
    <Drawer
      title={null}
      footer={null}
      closeable={true}
      maskCloseable={true}
      className={'fishery_profile__drawer'}
      open={isDrawerOpen}
      onClose={() =>
        history.replace(`/fishery/${fisheryPublicId}`, { hash: undefined })
      }
    >
      <BackButtonLayout
        useCrossIcon
        overlapHeader={matchPath(pathname, {
          path: [
            `/fishery/${fisheryPublicId}/lake`,
            `/fishery/${fisheryPublicId}/swimview`,
          ],
          exact: false,
        })}
        onBack={() =>
          history.replace(`/fishery/${fisheryPublicId}`, { hash: undefined })
        }
      >
        <div className={'main_container_padding'}>
          <div className={'main_container_padding__content'}>
            <Switch>
              <Route path={`/fishery/${fisheryPublicId}/overview`}>
                <Section title={'Overview'} noDivider>
                  <MarkdownContent
                    content={fisheryData.overview}
                    className={'fs-16 gRegular'}
                  />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/rules`}>
                <Section title={'Rules'} noDivider>
                  <MarkdownContent
                    className={'fs-16 gRegular'}
                    content={fisheryData?.rules || 'No rules available yet.'}
                  />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/sitemap`}>
                <Section title={'Site Map'} noDivider>
                  <PlaceholderImage
                    className={'drawer_image--full-width'}
                    src={fisheryData?.images?.sitemap}
                    alt={'Site Map for ' + fisheryData.name}
                  />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/swimview`}>
                <Section
                  title={
                    <span className={'fs-24'}>
                      {fisheryPublicId == exotic_fishery ? 'room' : 'swim'}
                      <span className={'sb-blue'}>view</span>
                    </span>
                  }
                  className={'mg-top-5'}
                  centreTitle
                  noDivider
                >
                  <div
                    className={
                      'white br10 sb-blue-bg pd-15 gMedium txt-center fs-17'
                    }
                  >
                    {` ${
                      fisheryPublicId == exotic_fishery
                        ? 'Roomview'
                        : 'Swimview'
                    } enables you to view every swim on the complex.
                    Simply choose your lake and select one of the swims to view
                    it full screen.`}
                  </div>
                  <SwimViewer
                    swimViews={fisheryData.lakes
                      .filter((lake) => !lake.accommodation)
                      .map((lake) => ({
                        name: lake.name,
                        swims: {
                          overlay: lake.images.primary,
                          swims: (lake?.swims || []).map((swim, i) => ({
                            name: swim?.name || 'Swim ' + (i + 1),
                            image: swim.image,
                          })),
                        },
                      }))}
                    fisheryPublicId={fisheryPublicId}
                  />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/how-to-book`}>
                <Section
                  title={'How to book on swimbooker'}
                  level={3}
                  noDivider
                >
                  <HowToBookGuide
                    goToCreateAccount={() => {
                      if (isLoggedIn) {
                        notification.success({
                          message: <div className='gSemiBold'>Success!</div>,
                          style: { border: '1px #2CB972 solid' },
                          placement:
                            window?.innerWidth <= 500 ? 'bottom' : 'topRight',
                          className: 'gRegular br10',
                          description: 'You are already logged in',
                        });
                        return;
                      }
                      history.replace(`/fishery/${fisheryPublicId}`, {
                        hash: undefined,
                      });
                      setFooterContentsKey(FooterContentsOption.CreateAccount);
                    }}
                  />
                </Section>
              </Route>
              <Route path={`/fishery/${fisheryPublicId}/live-availability`}>
                <SubSection title={'Live Availability Calendar'} noDivider>
                  <LiveAvailability
                    footerContents={footerContentsKey}
                    setFooterContents={setFooterContentsKey}
                    onDrawerClose={() =>
                      setFooterContentsKey(getDefaultFooter(isLoggedIn))
                    }
                    fisheryPublicId={fisheryPublicId}
                  />
                </SubSection>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/weather`}>
                <Section title={'Local Weather'} noDivider>
                  <SBOpenWeather
                    city={fisheryData.address.city}
                    location={fisheryData.address.location}
                  />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/reviews/:review_id`}>
                <Section title={'Full Review'} noDivider>
                  <SingleReview reviewsData={fisheryData.comments} />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/reviews`} exact>
                <Section title={'Reviews'} noDivider>
                  <ReviewsHeader
                    score={fisheryData.rating.score}
                    numReviews={fisheryData.comments.length}
                  />
                  <ReviewsList reviewsData={fisheryData.comments} />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/new-review`}>
                <Section title={'Leave a review'} noDivider>
                  <NewReviewForm
                    fisheryUUID={fisheryData?.fishery_id}
                    onSuccess={() => {
                      history.replace(
                        `/fishery/${fisheryPublicId}/review-success`
                      );
                    }}
                  />
                </Section>
              </Route>

              <Route path={`/fishery/${fisheryPublicId}/review-success`}>
                <Result
                  className={'custom_result'}
                  status='success'
                  title='Review Submitted!'
                  subTitle='Great! Thanks for sharing your experience. To return to the profile, simply press the arrow icon.'
                />
              </Route>

              <Route
                path={`/fishery/${fisheryPublicId}/lake/:lake_id/:drawer_key?`}
              >
                <LakeDetails
                  fisheryPublicId={fisheryPublicId}
                  lakes={fisheryData?.lakes || []}
                />
              </Route>
            </Switch>
          </div>
        </div>
      </BackButtonLayout>
    </Drawer>
  );

  const bottomButtonsStyle =
    ' gSemiBold br10 fs-20 white txt-center w-full pd-10 mg-bottom-10 h-fit ';

  const nameComponent = (
    <h1 className={'gSemiBold fs-30 mg-bottom-5'}>{fisheryData.venue_name}</h1>
  );
  const reviewsComponent = (
    <CountedReviewsScore
      score={fisheryData.rating.score}
      numReviews={fisheryData?.comments?.length}
      numRatings={fisheryData.rating?.total_reviews}
      reviewsAnchor={'#reviews'}
    />
  );

  const addressComponent = (
    <div className='mg-top-10'>
      <HashLink to={'#general'} className='black gMedium fs-15' replace>
        {address || ''}
      </HashLink>
      <div>
        <div className='pro-waitlist-button gBold'>
          <a href={`${process.env.REACT_APP_SBPLUS_URL}/sign-up`}>
            Cashback on bookings? Click here
          </a>
        </div>
      </div>
    </div>
  );

  const desktopHeader = (
    <Col
      xs={0}
      md={24}
      className={'mg-bottom-20 mg-top-15 fishery_profile__desktop__header'}
    >
      <div className={'fishery_profile__desktop__header__content'}>
        {nameComponent}
        {reviewsComponent}
        {addressComponent}
      </div>
      <div className={'fishery_profile__desktop__actions'}>
        <div className={'fishery_profile__desktop__actions__row'}>
          <LabelledRoundButton
            icon={<ShareAltOutlined />}
            label='Share'
            onClick={() => setShowShareModal(true)}
          />
          <LabelledRoundButton
            icon={
              fisheryData?.is_favourite ? (
                <HeartFilled className='sb-color-red' />
              ) : (
                <HeartOutlined />
              )
            }
            label='Favourite'
            onClick={() =>
              fisheryData?.fishery_id &&
              markAsFavourite(fisheryData?.fishery_id)
            }
          />
        </div>
        <div className={'fishery_profile__desktop__actions__row'}>
          <FisheryProfileQuickLinks
            isPackageBooking={isPackageBooking}
            small
            fisheryPublicId={fisheryPublicId}
            isDirectoryProfile={isDirectoryProfile}
          />
        </div>
      </div>
    </Col>
  );

  const mobileHeader = (
    <Col xs={24} md={0}>
      <div className='fishery_profile__header' style={{ position: 'relative' }}>
        {nameComponent}
        {reviewsComponent}
        {addressComponent}

        <Divider
          type={'horizontal'}
          className='fishery_profile__header__divider__xs'
        />

        <FisheryProfileQuickLinks
          fisheryPublicId={fisheryPublicId}
          isDirectoryProfile={isDirectoryProfile}
        />
      </div>

      <Divider />
    </Col>
  );

  const desktopGallery = (
    <Col xs={0} md={24} className='mg-bottom-30 mg-top-30'>
      <PrimarySecondaryImageGallery
        images={[
          fisheryData?.images?.primary,
          ...(fisheryData?.images?.secondary || []),
        ].filter((src) => src)}
      />
    </Col>
  );

  const mobileGallery = (
    <Col xs={24} md={0}>
      <div className='fishery_profile__top_image_gallery'>
        <ImageSlider
          images={[
            fisheryData?.images?.primary,
            ...(fisheryData?.images?.secondary || []),
          ]
            .filter((src) => src)
            .map((src) => ({ src, alt: fisheryData.venue_name }))}
          loading={!fisheryData}
          openOnClick
        />
      </div>
    </Col>
  );

  return (
    <MainPageBackButtonLayout
      overlapHeader
      onBack={
        () =>
          history.push(
            '/search'
          ) /* TODO is there any way to detect if the previous page in the history was /search?something and disable this??*/
      }
      showFavouriteButton
      onFavourite={
        fisheryData?.fishery_id &&
        (() => markAsFavourite(fisheryData?.fishery_id))
      }
      showShareButton
      onShare={() => setShowShareModal(true)}
      isFavourite={fisheryData?.is_favourite}
    >
      <SocialMediaShareModal
        url={window?.location?.href}
        pageTitle={`${fisheryData.venue_name} Profile on Swimbooker.com`}
        isOpen={showShareModal}
        setIsOpen={setShowShareModal}
      />
      <MembershipDrawer
        isOpen={membershipDrawerOpen}
        alreadyMember={alreadyMember}
        fisheryId={fisheryId}
        membershipForm={membershipForm?.length > 0 ? membershipForm[0] : []}
        setIsOpen={setMembershipDrawerOpen}
      />
      {readMoreDrawer}
      <FisheryProfileFooterBar
        footerContents={footerContentsKey}
        isPackageBooking={isPackageBooking}
        setFooterContents={setFooterContentsKey}
        onDrawerClose={() => setFooterContentsKey(getDefaultFooter(isLoggedIn))}
        fisheryPublicId={fisheryPublicId}
        isComingSoon={fisheryData?.is_coming_soon}
      />
      <div
        className={'main_container_padding'}
        id={'fishery_profile_main_container'}
      >
        <div className={'main_container_padding__content'}>
          <Helmet>
            <meta charSet='utf-8' />
            <meta
              name='description'
              content={`Welcome to ${fisheryData.venue_name}'s fishery profile. Check out the fishery's details, rules and image gallery. If you've visited this venue, don't forget to leave a review.`}
            />
            <title>
              {fisheryData.venue_name} Profile | Book Fishing & Learn More About
              The Fishery{' '}
            </title>
          </Helmet>
          {desktopHeader}
          {desktopGallery}
          {mobileGallery}
          {mobileHeader}

          <Section
            title={'Quick Facts'}
            extra={
              <BookableTag
                isBookable={fisheryData.is_bookable}
                isComingSoon={fisheryData?.is_coming_soon}
              />
            }
          >
            <FeatureSummary features={fisheryData.quick_facts} />
          </Section>

          {!isDirectoryProfile && (
            <Section
              title={
                fisheryPublicId == exotic_fishery ||
                fisheryData.lakes.filter((lake) => lake.accommodation).length >
                  0
                  ? 'What Can I Book?'
                  : 'Where Can I Fish?'
              }
            >
              <LakesSlider
                lakesData={fisheryData.lakes}
                fisheryPublicId={fisheryPublicId}
                loading={!fisheryData}
              />
            </Section>
          )}

          {Array.isArray(fisheryMembershipData) &&
            fisheryMembershipData.length > 0 && (
              <div id='membership'>
                <div id='seasonTicket'>
                  <Section
                    Divider
                    title={
                      is_sandhurst
                        ? 'Memberships & Winter Tickets'
                        : 'Memberships & Season Tickets'
                    }
                  >
                    <div className='row-center gap-2r flex-wrap mg-top-30 mg-bottom-40'>
                      {fisheryMembershipData.find(
                        (item) => item.type === 'member'
                      ) !== undefined &&
                        fisheryMembershipData.find(
                          (item) => item.type === 'member'
                        ).membership_plans?.length > 0 && (
                          <div
                            className='fishery-membership-button-container row-center cp'
                            onClick={() => {
                              const hasFisheryId =
                                userMembershipData?.memberships.filter(
                                  (data) =>
                                    data.fishery_id == fisheryId &&
                                    data?.status !== 'Deleted'
                                );
                              setMembershipForm(
                                fisheryMembershipData.filter(
                                  (item) => item.type === 'member'
                                )
                              );

                              setalreadyMember(hasFisheryId);
                              setMembershipDrawerOpen(true);
                            }}
                          >
                            <div className='sb-blue-bg-500  fishery-membership-button-icon'>
                              <img src={memberIcon} alt='membership-icon' />
                            </div>
                            <div className='gBold fs-20  justify-between fishery-membership-button'>
                              <div>Become a member</div>
                              <div className='gSemiBold row-center fs-16 sb-blue-600'>
                                Apply
                                <img
                                  src={ArrowRight}
                                  alt='right-arrow'
                                  className='mg-left-10'
                                  width={7}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      {fisheryMembershipData.find(
                        (item) => item.type === 'season_ticket'
                      ) !== undefined &&
                        fisheryMembershipData.find(
                          (item) => item.type === 'season_ticket'
                        ).membership_plans?.length > 0 && (
                          <div
                            className='fishery-membership-button-container row-center cp'
                            onClick={() => {
                              const hasFisheryId =
                                userMembershipData?.season_tickets.filter(
                                  (data) =>
                                    data.fishery_id == fisheryId &&
                                    data?.status !== 'Deleted'
                                );
                              setMembershipForm(
                                fisheryMembershipData.filter(
                                  (item) => item.type === 'season_ticket'
                                )
                              );

                              setalreadyMember(hasFisheryId);
                              setMembershipDrawerOpen(true);
                            }}
                          >
                            <div
                              className=' fishery-membership-button-icon'
                              style={{ background: '#28a973' }}
                            >
                              <img src={ticketIcon} alt='ticket-icon' />
                            </div>
                            <div className='gBold fs-20  justify-between fishery-membership-button'>
                              <div>
                                Purchase {is_sandhurst ? 'Winter' : 'Season'}{' '}
                                Ticket
                              </div>
                              <div className='gSemiBold fs-16 sb-blue-600 row-center'>
                                Buy
                                <img
                                  src={ArrowRight}
                                  alt='right-arrow'
                                  className='mg-left-10'
                                  width={7}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </Section>
                </div>
              </div>
            )}
          <Row gutter={[20, 20]}>
            {fisheryData?.is_bookable ? (
              europeanCountries?.includes(fisheryData?.country) && (
                <Col span={24} className='mg-bottom-20'>
                  <div
                    className='white cp h-full fishery-AP-banner justify-between br10 row-center'
                    style={{
                      position: 'relative',
                      backgroundImage: `url(${ship})`,
                    }}
                    onClick={() => {
                      dispatch({
                        type: TOGGLE_AP_CROSSING,
                        showCrossingDrawer: true,
                      });
                    }}
                  >
                    <div
                      className='br8 w-full h-full'
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.60)',
                        zIndex: 1,
                      }}
                    />

                    <div
                      className='h-full  justify-between ap-title mg-right-20'
                      style={{
                        display: 'flex',

                        flex: 1,
                        zIndex: 2,
                      }}
                    >
                      <div className='fs-18 gBold'>
                        Interested in{' '}
                        <span className='gMedium sb-blue-100'>
                          {fisheryData?.venue_name}? 👀
                        </span>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div className='fs-16 gMedium'>
                          We can help{' '}
                          <span className='gBold'>
                            save on your next crossing
                          </span>{' '}
                          to Europe
                        </div>
                      </div>
                    </div>
                    <img src={rightArrow} width={23} style={{ zIndex: 2 }} />
                  </div>
                </Col>
              )
            ) : (
              <Col span={24} className='mg-bottom-20'>
                <div
                  className='white cp h-full fishery-AP-banner justify-between br10 row-center'
                  style={{
                    position: 'relative',
                    backgroundImage: `url(${shop})`,
                  }}
                  onClick={() => {
                    window.open('https://shop.swimbooker.com/', '_blank');
                  }}
                >
                  <div
                    className='br8 w-full h-full'
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.60)',
                      zIndex: 1,
                    }}
                  />

                  <div
                    className='h-full  justify-between ap-title mg-right-20'
                    style={{
                      display: 'flex',

                      flex: 1,
                      zIndex: 2,
                    }}
                  >
                    <div className='fs-18 gBold'>
                      <span className='gMedium sb-blue-100'>Need some</span>{' '}
                      Last minute{' '}
                      <span className='gMedium sb-blue-100'>supplies? 🪝</span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div className='fs-16 gMedium'>
                        The SB shop <span className='gBold'>SB shop</span> has
                        everything you need!
                      </div>
                    </div>
                  </div>
                  <img src={rightArrow} width={23} style={{ zIndex: 2 }} />
                </div>
              </Col>
            )}
          </Row>
          <Section title={'Location Overview'}>
            <MarkdownContent
              className={'fs-16 gRegular'}
              content={fisheryData.overview.substring(0, 450) + '...'}
            />
            {[
              { href: '/overview', text: 'Read more' },
              ...(!isDirectoryProfile
                ? [
                    {
                      href: '/rules',
                      text: 'Check location rules',
                    },
                  ]
                : []),
            ].map((data, i) =>
              !data.disabled ? (
                <p className={'mg-bottom-12 fs-16 gSemiBold'} key={`Link ${i}`}>
                  <Link
                    to={`/fishery/${fisheryPublicId}${data.href}`}
                    className={' black txt-underline'}
                  >
                    <span>{data.text}</span>
                    <InlineChevronIcon />
                  </Link>
                </p>
              ) : (
                <p className={'mg-bottom-12 fs-16 gRegular light-grey2'}>
                  <span>{data.text}</span>
                  <InlineChevronIcon />
                </p>
              )
            )}
          </Section>

          {!isDirectoryProfile && fisheryPublicId !== exotic_fishery && (
            <Section title={'SB Media'}>
              <div
                className={`fishery_profile__media_wrapper ${
                  fisheryData?.media?.virtual_experience &&
                  fisheryData?.media?.sb_diaries &&
                  fisheryData?.media?.sb_flythrough
                    ? 'media_wrapper_check'
                    : ''
                }`}
              >
                {fisheryData?.lakes?.filter((item) => !item.accommodation)
                  .length > 0 &&
                  !fisheryData?.hide_swimview && (
                    <SubSection
                      title={
                        <span>
                          {fisheryPublicId == exotic_fishery ? 'room' : 'swim'}
                          <span className={'sb-blue'}>view</span>&#8482;
                        </span>
                      }
                    >
                      <p className={'gSemiBold fs-16'}>
                        {`Plan your trip ahead. Take a look at ${
                          fisheryPublicId == exotic_fishery
                            ? 'the rooms available on the complex.'
                            : `every swim on the complex via our SwimView™ gallery.`
                        }`}
                      </p>
                      <Link
                        to={`/fishery/${fisheryPublicId}/swimview`}
                        className='fs-16 gSemiBold black txt-underline'
                      >
                        Check it out here
                        <InlineChevronIcon />
                      </Link>
                    </SubSection>
                  )}
                {fisheryData?.media?.virtual_experience && (
                  <SubSection
                    title={
                      <span className={'dark-green'}>Virtual Experience</span>
                    }
                  >
                    <p className={'gSemiBold fs-16'}>
                      Step inside our industry first Virtual Tour. Walk around
                      and get a sense for what the venue has to offer.
                    </p>
                    <a
                      href={fisheryData.media.virtual_experience}
                      className={'fs-16 gSemiBold black txt-underline'}
                    >
                      Check it out here
                      <InlineChevronIcon />
                    </a>
                  </SubSection>
                )}
                {fisheryData?.media?.sb_diaries && (
                  <SubSection
                    title='SB Diaries'
                    extra={
                      <img
                        alt='YouTube Icon'
                        src={youtubeIcon}
                        style={{ height: '1.5rem', width: 'auto' }}
                      />
                    }
                  >
                    <p className='gSemiBold fs-16'>
                      A video series dedicated to showing anglers all about a
                      fishery; including facts and facilities.
                    </p>
                    <a
                      href={fisheryData.media.sb_diaries}
                      className={'fs-16 gSemiBold black txt-underline'}
                    >
                      Check it out here
                      <InlineChevronIcon />
                    </a>
                  </SubSection>
                )}
                {fisheryData?.media?.sb_flythrough && (
                  <SubSection title='SB Flythrough'>
                    <p className='gSemiBold fs-16'>
                      A brand new vantage point giving you the opportunity to
                      see your favourite venues from the skies!
                    </p>
                    <a
                      href={fisheryData.media.sb_flythrough}
                      className={'fs-16 gSemiBold black txt-underline'}
                    >
                      Check it out here
                      <InlineChevronIcon />
                    </a>
                  </SubSection>
                )}
              </div>
            </Section>
          )}
          <Section
            title={'Verified Catch Reports'}
            justifyTitleDiv='space-between'
            extra={
              <Row justify={'end'}>
                <Col xs={0} sm={24}>
                  <Button
                    className={
                      'sb-blue-bg ' +
                      ' fishery_profile__footer__button  gMedium brFull fs-18 white txt-center h-full pd-10 pd-left-28 pd-right-28 antd-link-button-override'
                    }
                    onClick={() => {
                      if (isLoggedIn) {
                        dispatch({
                          type: TOGGLE_CATCH_REPORT,
                          showCatchReportDrawer: true,
                          isRequestFromProfile: true,
                          fisheryData: {
                            name: fisheryData?.venue_name,
                            publicId: fisheryPublicId,
                            id: fisheryData?.fishery_id,
                          },
                        });
                      } else {
                        notification.warning({
                          message: (
                            <div className='gSemiBold'>Please be aware!</div>
                          ),
                          style: { border: '1px #F7C046 solid' },
                          className: 'gRegular br10',
                          description: 'Please login to share catch report',
                          placement:
                            window?.innerWidth <= 500 ? 'bottom' : 'topRight',
                        });
                      }
                    }}
                  >
                    + Submit New Report
                  </Button>
                </Col>
              </Row>
            }
          >
            {catchReport && (
              <CatchReportSlider
                lakesData={catchReport}
                fisheryPublicId={fisheryPublicId}
                loading={!catchReport}
              />
            )}
            {
              <Col sm={0} xs={24}>
                <Row justify='center'>
                  <Button
                    className={
                      'sb-blue-bg ' +
                      ' fishery_profile__footer__button  gMedium brFull fs-18 white txt-center h-full pd-10 pd-left-28 pd-right-28 antd-link-button-override'
                    }
                    onClick={() => {
                      if (isLoggedIn) {
                        dispatch({
                          type: TOGGLE_CATCH_REPORT,
                          showCatchReportDrawer: true,
                          isRequestFromProfile: true,
                          fisheryData: {
                            name: fisheryData?.venue_name,
                            publicId: fisheryPublicId,
                            id: fisheryData?.fishery_id,
                          },
                        });
                      } else {
                        notification.warning({
                          message: (
                            <div className='gSemiBold'>Please be aware!</div>
                          ),
                          style: { border: '1px #F7C046 solid' },
                          className: 'gRegular br10',
                          description: 'Please login to share catch report',
                          placement:
                            window?.innerWidth <= 500 ? 'bottom' : 'topRight',
                        });
                      }
                    }}
                  >
                    + Submit New Report
                  </Button>
                </Row>
              </Col>
            }
          </Section>

          {!isDirectoryProfile && (
            <Section title='Facilities'>
              <FeatureSummary features={fisheryData.facilities} wide />
            </Section>
          )}

          <Section id={'general'} title={'General Details'}>
            <div className={'fishery_profile__contact_wrapper'}>
              <div
                className={
                  'fishery_profile__contact_wrapper__map br15 mg-bottom-10'
                }
              >
                <MapWithAddressPin
                  address={fisheryData.address}
                  disableInteraction
                />
              </div>
              <ContactInfo
                contact={fisheryData.contact}
                what3words={fisheryData?.what3words?.words}
              />
            </div>
          </Section>
          {fisheryData?.public_id !== 11011 &&
            fisheryData?.public_id !== 12374 && (
              <Section id={'reviews'} title={'Reviews'} noDivider>
                <ReviewsHeader
                  score={fisheryData.rating.score}
                  numReviews={
                    fisheryData?.comments?.length ||
                    fisheryData.rating.total_reviews
                  }
                />
                <ReviewsSlider
                  reviewsData={fisheryData?.comments}
                  fisheryPublicId={fisheryPublicId}
                  loading={!fisheryData}
                />
                <div className={'fishery_profile__reviews_buttons mg-top-10'}>
                  {isLoggedIn ? (
                    <Link
                      passHref
                      to={`/fishery/${fisheryPublicId}/new-review`}
                    >
                      <Button
                        className={'light-green-bg ' + bottomButtonsStyle}
                      >
                        Leave a review
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className={'bg-light-grey-2 ' + bottomButtonsStyle}
                      disabled
                    >
                      Log in to leave a review
                    </Button>
                  )}
                  <Link passHref to={`/fishery/${fisheryPublicId}/reviews`}>
                    <Button className={'sb-blue-bg ' + bottomButtonsStyle}>
                      Show all reviews
                    </Button>
                  </Link>
                </div>
              </Section>
            )}
        </div>
      </div>
    </MainPageBackButtonLayout>
  );
}
